/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-extraneous-dependencies */
import { createAvatar } from '@dicebear/core';
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as collection from '@dicebear/collection';
import getBootstrapData from 'src/utils/getBootstrapData';

export type AvatarStyle =
  | 'bottts' // 可爱机器人
  | 'identicon' // GitHub 经典风格
  | 'shapes' // 彩色几何图形
  | 'initials' // 用户首字母
  | 'avataaars' // 卡通人物
  | 'pixelArt'; // 像素风

interface GenerateDefaultAvatarOptions {
  seed: string;
  size?: number;
  style?: AvatarStyle;
}

/**
 * 生成默认头像
 * @param options.seed - 种子字符串（通常是用户name或邮箱）
 * @param options.size - 头像尺寸，默认 140
 * @param options.style - 头像风格，默认 'bottts'（可爱机器人）
 * @returns Data URI 格式的头像图片
 */
export function generateDefaultAvatar({
  seed,
  size = 140,
  style = 'bottts',
}: GenerateDefaultAvatarOptions): string {
  const styleMap: Record<AvatarStyle, unknown> = {
    bottts: collection.bottts,
    identicon: collection.identicon,
    shapes: collection.shapes,
    initials: collection.initials,
    avataaars: collection.avataaars,
    pixelArt: collection.pixelArt,
  };

  const avatar = createAvatar(styleMap[style] as any, {
    seed,
    size,
    // bottts 风格的额外配置
    ...(style === 'bottts' && {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
    }),
    // initials 风格的额外配置
    ...(style === 'initials' && {
      backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
      fontSize: 45,
    }),
  });

  return avatar.toDataUri();
}

/**
 * 获取用户头像 URL（带默认头像回退）
 * @param userKey - 用户标识（username 或 email）
 * @param customAvatarUrl - 自定义头像 URL
 * @param hasError - 自定义头像是否加载失败
 * @param style - 默认头像风格
 * @returns 头像 URL
 */
export function getAvatarUrl(
  userKey: string | undefined,
  customAvatarUrl: string | undefined,
  hasError: boolean,
  style: AvatarStyle = 'bottts',
): string {
  if (customAvatarUrl && !hasError) {
    return customAvatarUrl;
  }
  return generateDefaultAvatar({
    seed: userKey || 'default-user',
    style,
  });
}

interface ResolveUserAvatarOptions extends GenerateDefaultAvatarOptions {
  userId: number;
  customAvatarUrl?: string;
}

interface AvatarUpdatedEventDetail {
  key: number;
  userId?: number;
}

const userAvatarAvailabilityCache = new Map<number, boolean>();
const userAvatarAvailabilityRequests = new Map<number, Promise<boolean>>();
const userAvatarCacheKeys = new Map<number, number>();

function getResolvedAvatarUserId(userId?: number): number | undefined {
  if (userId !== undefined) {
    return userId;
  }
  const currentUserId = getBootstrapData().user?.userId;
  return typeof currentUserId === 'number' ? currentUserId : undefined;
}

function getAvatarCacheKey(userId?: number): number | undefined {
  const resolvedUserId = getResolvedAvatarUserId(userId);
  if (resolvedUserId === undefined) {
    return undefined;
  }
  return userAvatarCacheKeys.get(resolvedUserId) ?? resolvedUserId;
}

export function markUserAvatarUpdated(
  userId?: number,
  cacheKey: number = Date.now(),
): AvatarUpdatedEventDetail {
  const resolvedUserId = getResolvedAvatarUserId(userId);
  if (resolvedUserId !== undefined) {
    userAvatarCacheKeys.set(resolvedUserId, cacheKey);
    userAvatarAvailabilityCache.delete(resolvedUserId);
    userAvatarAvailabilityRequests.delete(resolvedUserId);
  }
  return resolvedUserId === undefined
    ? { key: cacheKey }
    : { key: cacheKey, userId: resolvedUserId };
}

export function getCurrentUserAvatarApiUrl(cacheKey?: number): string {
  const resolvedCacheKey = cacheKey ?? getAvatarCacheKey();
  return resolvedCacheKey === undefined
    ? '/api/v1/user_avatar/avatar'
    : `/api/v1/user_avatar/avatar?t=${resolvedCacheKey}`;
}

export function getUserAvatarApiUrl(userId: number, cacheKey?: number): string {
  const resolvedCacheKey = cacheKey ?? getAvatarCacheKey(userId);
  return resolvedCacheKey === undefined
    ? `/api/v1/user_avatar/avatar/${userId}`
    : `/api/v1/user_avatar/avatar/${userId}?t=${resolvedCacheKey}`;
}

export async function hasCustomUserAvatar(userId: number): Promise<boolean> {
  const cached = userAvatarAvailabilityCache.get(userId);
  if (cached !== undefined) {
    return cached;
  }

  const pendingRequest = userAvatarAvailabilityRequests.get(userId);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = fetch(getUserAvatarApiUrl(userId), {
    credentials: 'same-origin',
    cache: 'no-store',
  })
    .then(response => response.status === 200)
    .catch(() => false)
    .then(hasAvatar => {
      userAvatarAvailabilityCache.set(userId, hasAvatar);
      return hasAvatar;
    })
    .finally(() => {
      userAvatarAvailabilityRequests.delete(userId);
    });

  userAvatarAvailabilityRequests.set(userId, request);
  return request;
}

export async function resolveUserAvatarUrl({
  userId,
  seed,
  size = 140,
  style = 'bottts',
  customAvatarUrl,
}: ResolveUserAvatarOptions): Promise<string> {
  if (customAvatarUrl) {
    return customAvatarUrl;
  }

  const hasAvatar = await hasCustomUserAvatar(userId);

  if (hasAvatar) {
    return getUserAvatarApiUrl(userId);
  }

  return generateDefaultAvatar({
    seed,
    size,
    style,
  });
}
